import { useState } from "react";
import { GoDownload } from "react-icons/go";
import { IoMdArrowDropdown, IoMdArrowDropup } from "react-icons/io";
import Spin from "../Spinner/Spinner";
import {
  CategoryMultiSelect,
  PriceSlider,
  SearchInput,
  SortDropdown,
  SubcategoryMultiSelect,
} from "./Filters";
import { useProductListing } from "./hooks/useProductListing";
import styles from "./Product.module.css";

export const ProductListing = () => {
  const {
    products,
    loading,
    filters,
    handleFiltersChange,
    handleSortSelect,
    selectedSubCategories,
    categories,
    selectedProducts,
    setSelectedProducts,
    openedCategories,
    setOpenedCategories,
    getCategoryProducts,
    handleContinue,
    handleExport,
  } = useProductListing();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogData, setDialogData] = useState(null); // categoryWise array
  const [customPrices, setCustomPrices] = useState({}); // { [productId]: price }

  const onContinue = () => {
    const categoryWise = handleContinue();
    if (!categoryWise) return;
    // prefill prices from product data
    const prices = {};
    categoryWise.forEach(({ products: prods }) => {
      prods.forEach((p) => {
        prices[p._id] = p.price?.[0]?.cost ?? 0;
      });
    });
    setCustomPrices(prices);
    setDialogData(categoryWise);
    setDialogOpen(true);
  };

  const onExportFromDialog = () => {
    handleExport(customPrices);
  };

  const handleSelect = (productId) => {
    setSelectedProducts((prev) => {
      if (prev.includes(productId)) {
        return prev.filter((id) => id !== productId);
      }
      return [...prev, productId];
    });
  };

  if (loading.initialLoading) {
    return <Spin />;
  }
  if (loading.initialLoading && loading.loading) {
    return <Spin />;
  }

  return (
    <>
    <div className={styles.mainContainer}>
      <div className={styles.container}>
        <div
          style={{
            width: "100%",
            display: "flex",
            flexDirection: "column",
            gap: "20px",
          }}
        >
          <div className={styles.filtersContainer}>
            <SearchInput
              value={filters.search}
              onChange={(value) => handleFiltersChange("search", value)}
            />

            <button
              className={styles.button}
              onClick={() =>
                handleFiltersChange(
                  "moreFiltersHidden",
                  !filters.moreFiltersHidden
                )
              }
              style={{
                color: !filters.moreFiltersHidden ? "#A8A8A8" : "#000",
                borderColor: !filters.moreFiltersHidden ? "#A8A8A8" : "#000",
              }}
            >
              Filters
            </button>
            <SortDropdown
              sortBy={filters.sortBy}
              sortOrder={filters.sortOrder}
              onChange={handleSortSelect}
            />
            <button className={styles.exportButton} onClick={onContinue}>
              Continue ({selectedProducts.length})
              <GoDownload fontSize={20} />
            </button>
          </div>
          {filters.moreFiltersHidden && (
            <div
              className={styles.filtersContainer}
              style={{
                justifyContent: "start",
                gap: "8px",
              }}
            >
              <CategoryMultiSelect
                value={filters.category}
                onChange={(value) => {
                  handleFiltersChange("category", value);

                  const selectedCategoryIds = (value || []).map(String);
                  const allSubIds = categories
                    .filter((c) => selectedCategoryIds.includes(String(c._id)))
                    .flatMap((c) => c.subCategories || [])
                    .map((s) => String(s._id));

                  const uniqueSubIds = Array.from(new Set(allSubIds));

                  handleFiltersChange("subcategory", uniqueSubIds);
                }}
                categories={categories}
              />
              <SubcategoryMultiSelect
                value={filters.subcategory}
                onChange={(value) => handleFiltersChange("subcategory", value)}
                subCategories={selectedSubCategories}
              />
              <PriceSlider
                value={filters.priceRange}
                onChange={(value) => handleFiltersChange("priceRange", value)}
              />
            </div>
          )}
        </div>
        <div className={styles.productContainer}>
          {categories.map((category) => {
            const categoryProducts = getCategoryProducts(category._id);
            if (!categoryProducts || categoryProducts.length === 0) {
              return null;
            }

            const idStr = String(category._id);
            const isOpen = openedCategories.includes(idStr);

            const categoryProductIds = categoryProducts.map((p) => p._id);
            const selectedCount = categoryProductIds.filter((id) =>
              selectedProducts.includes(id)
            ).length;
            const allSelected =
              categoryProductIds.length > 0 &&
              selectedCount === categoryProductIds.length;

            const toggleCategory = () => {
              setOpenedCategories((prev) =>
                prev.includes(idStr)
                  ? prev.filter((cId) => cId !== idStr)
                  : [...prev, idStr]
              );
            };

            const handleCategorySelectToggle = () => {
              setSelectedProducts((prev) => {
                const set = new Set(prev);

                if (allSelected) {
                  categoryProductIds.forEach((id) => set.delete(id));
                } else {
                  categoryProductIds.forEach((id) => set.add(id));
                }

                return Array.from(set);
              });
            };

            return (
              <div key={category._id} className={styles.categorySection}>
                <div className={styles.categoryHeader} onClick={toggleCategory}>
                  <span className={styles.categoryTitle}>{category.name}</span>
                  {isOpen ? (
                    <IoMdArrowDropup size={20} />
                  ) : (
                    <IoMdArrowDropdown size={20} />
                  )}
                </div>

                {isOpen && (
                  <>
                    <div className={styles.categorySelectRow}>
                      <label className={styles.categorySelectLabel}>
                        <input
                          type="checkbox"
                          checked={allSelected}
                          onChange={handleCategorySelectToggle}
                        />
                        ({" "}
                        <span>
                          {selectedCount} of {categoryProducts.length} selected
                        </span>
                        )
                      </label>
                    </div>
                    <div className={styles.categoryProducts}>
                      {categoryProducts.map((product) => (
                        <ProductCard
                          key={product._id}
                          title={product.name}
                          brand={product.brandName}
                          price={product.price?.[0]?.cost ?? 0}
                          description={product.description}
                          itemDetails={product.item_Details}
                          isSelected={selectedProducts.includes(product._id)}
                          handleSelect={() => handleSelect(product._id)}
                          image={product.image?.[0]}
                        />
                      ))}
                    </div>
                  </>
                )}
              </div>
            );
          })}
          {products.length === 0 && (
            <div className={styles.noProducts}>No products found</div>
          )}
        </div>
      </div>
    </div>

    {dialogOpen && dialogData && (
      <div style={overlayStyle} onClick={() => setDialogOpen(false)}>
        <div style={modalStyle} onClick={(e) => e.stopPropagation()}>
          <button onClick={() => setDialogOpen(false)} style={closeBtnStyle}>✕</button>
          <div style={{ marginBottom: 24 }}>
            <span style={{ fontSize: 20, fontWeight: 700, color: "#0f143a", fontFamily: "Nunito" }}>
              Review & Edit Prices
            </span>
          </div>

          <div style={{ overflowY: "auto", maxHeight: "55vh", display: "flex", flexDirection: "column", gap: 12 }}>
            {dialogData.map(({ category, products: prods }) => (
              <div key={category._id}>
                <div style={{ fontSize: 14, fontWeight: 700, color: "#0f143a", fontFamily: "Nunito", marginBottom: 8 }}>
                  {category.name}
                </div>
                {prods.map((product) => (
                  <div key={product._id} style={dialogRowStyle}>
                    <img
                      src={product.image?.[0]}
                      alt={product.name}
                      style={{ width: 52, height: 52, objectFit: "contain", borderRadius: 8, flexShrink: 0 }}
                    />
                    <span style={{ flex: 1, fontSize: 14, fontFamily: "Nunito", color: "#0f143a" }}>
                      {product.name}
                    </span>
                    <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                      <span style={{ fontSize: 14, fontFamily: "Nunito" }}>₹</span>
                      <input
                        type="number"
                        min={0}
                        value={customPrices[product._id] ?? ""}
                        onChange={(e) =>
                          setCustomPrices((prev) => ({ ...prev, [product._id]: e.target.value }))
                        }
                        style={priceInputStyle}
                      />
                    </div>
                  </div>
                ))}
              </div>
            ))}
          </div>

          <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 24 }}>
            <button className={styles.exportButton} onClick={onExportFromDialog}>
              Export PDF <GoDownload fontSize={18} />
            </button>
          </div>
        </div>
      </div>
    )}
    </>
  );
};

const overlayStyle = {
  position: "fixed",
  inset: 0,
  backgroundColor: "rgba(0,0,0,0.45)",
  zIndex: 1000,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
};

const modalStyle = {
  backgroundColor: "#fff",
  borderRadius: 16,
  padding: "32px",
  width: "calc(100% - 32px)",
  maxWidth: 600,
  boxShadow: "0 8px 32px rgba(0,0,0,0.18)",
  fontFamily: "Nunito",
  position: "relative",
  margin: "0 16px",
};

const closeBtnStyle = {
  position: "absolute",
  top: 12,
  right: 12,
  background: "none",
  border: "none",
  fontSize: 20,
  cursor: "pointer",
  color: "#555",
  padding: "4px 8px",
  lineHeight: 1,
};

const dialogRowStyle = {
  display: "flex",
  alignItems: "center",
  gap: 14,
  padding: "10px 0",
  borderBottom: "1px solid #f0f0f0",
};

const priceInputStyle = {
  width: 100,
  border: "1px solid #dbdbdb",
  borderRadius: 8,
  padding: "6px 10px",
  fontSize: 14,
  fontFamily: "Nunito",
  textAlign: "right",
};

const ProductCard = ({
  title,
  brand,
  price,
  description,
  itemDetails,
  isSelected,
  handleSelect,
  image,
}) => {
  return (
    <div className={styles.productCard} onClick={handleSelect}>
      <input type="checkbox" className={styles.checkbox} checked={isSelected} />
      <div className={styles.imageContainer}>
        <img src={image} alt={title} className={styles.productImage} />
        <div className={styles.textContainer}>
          <div className={styles.heading}>{title}</div>
          <div className={styles.brand}>{brand}</div>
          <div className={styles.descriptionHeading}>Description</div>
          <div className={styles.descriptionText}>{description}</div>
          <div className={styles.descriptionHeading}>Item Details</div>
          <div className={styles.descriptionText}>
            {itemDetails.map((item, index) => (
              <div key={index} className={styles.descriptionText}>
                {index + 1}. {item}
              </div>
            ))}
          </div>
        </div>
      </div>
      <div className={styles.price}>₹{Number(price ?? 0)?.toFixed(2)}</div>
    </div>
  );
};
